import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Input from '../Input/Input';
import Tags from '../Tags/Tags';
import createFakeEvent from '../../lib/createFakeEvent';

const TAG_PREFIX = '#';
const DELIMITER = ' ';

/**
 * `TagInput` is a an {@link Input} field that can display tags in a dropdown and the selected tags to the left of the input
 * field.
 *
 * The tags can be deleted independently. All changes to the tag selection and input field will trigger an *onChange* event
 * where the value is of type string. Each tag will be prefixed by the *tagPrefix* prop character and you may specify how
 * tags and input value text is separated.
 *
 * By default tags are donated by `#` and a `space` is used as delimiter. For example, the following string value contains 3
 * tags and one freetext value:
 * ````
 * #tag-1 #tag-2 #tag-3 this-is-not-a-tag
 * ````
 * The component has three static methods that can be used to make it easier to parse the input value:
 * - `getSelectedTags()` returns an array of the tags. The *tagPrefix* or freetext value is not included.
 * - `getTagString()` constructs an input value based on a tag array, delimiter, tagPrefix and freetext.
 * - `getStrings()` returns an array of two strings where the first is the tags and the second is the freetext.
 *
 * An optional notice can be displayed below the input field. See {@link Notice} for the different types.
 *
 * There is a SASS mixin available for making tags have the same width: `tag-input-fixed-columns(columns)`. The input field
 * will be moved below the tags. If you are not using SASS and haven't imported the mixin file you can achieve the same by
 * setting `grid-template-columns` to the number of columns you want. Remember to also let the input field span all columns.
 *
 * `TagInput` supports all props available for the {@link Input}.
 *
 * @property {boolean} addable If set to `true`, the user will be able to create new tags that are not in *options*. It is up
 * to the app to add new values to the options. Defaults to `false` (only tags from *options* can be added).
 * @property {string} characterCountText The character count label. Defaults to "#".
 * @property {string} className The class name of this component. Will be appended to "core-tag-input". Component will also
 * get a "tags" class when tags are displayed.
 * @property {boolean} clearable If set to `true`, a **Clear** button will be shown on hover and focus. Defaults to `true`.
 * @property {string} delimiter The string used as delimiter between multiple tags and freetext. Defaults to a space.
 * You can set this prop to e.g. ", " to support spaces in tags.
 * @property {boolean} disabled The component will be disabled if this prop is set to `true`. Defaults to `false`.
 * @property {node} errorMessage An error {@link Notice} that will be shown below the component. Defaults to no Notice.
 * @property {string} iconName Custom icon that will be displayed to the right in the input field. The icon name can be any
 * of the embedded icons in {@link Icon}.
 * @property {string} id The ID of this component. Defaults to variant of the label provided.
 * @property {node} infoMessage An info Notice that will be shown below the component. Defaults to no Notice.
 * @property {string} labelKey The key to used to find the label if value is an object or *options* is an array of objects.
 * Defaults to "label".
 * @property {boolean} locked The input field will be disabled and show a small lock icon to the right of the input field.
 * @property {function} onChange Callback that is called when the user types something in the input field or selects a tag.
 * If the value was selected in the autocomplete dropdown, the callback will be passed a second parameter of value `true`.
 * @property {function} onClearNotice Callback that is called when the user dismiss the optional Notice.
 * @property {function} onDeleteTag A callback that is called when the user has clicked on a tag to delete it. This callback
 * should only be used in rare cases where you need to know exactly which tag was deleted. The *onChange* callback will also
 * be invoked with the deleted tag already removed from the value in the event.
 * @property {function} onKeyDown A callback that is called whenever the user has pressed a key. Use this callback to handle
 * Enter keys etc.
 * @property {function} onUnlock A callback that is called whenever the user has clicked on the lock icon. You can use this
 * to unlock all locked fields in a form.
 * @property {array} options An array of strings or objects that defines the available tags. Supports both array of
 * strings and objects. See *labelKey* and *valueKey*. Defaults to [].
 * @property {string} placeholder The placeholder of the input field. Defaults to no placeholder.
 * @property {boolean} required If set to `true`, a required indicator will be displayed inside the input field. Defaults to
 * `false`.
 * @property {boolean} showCharacterCount If set to true, it will show the character count on text input field types. Defaults to `false`.
 * @property {boolean} showOptionsOnFocus By default tags are only shown when the user has typed something. Setting
 * this prop to `true` will result in the tags dropdown to be displayed when the input field gets the focus. Defaults to
 * `false`.
 * @property {boolean} showSearch If set to `true`, a search icon is displayed to the left of the input field. No search icon
 * is displayed by default.
 * @property {boolean} showUnderline The component will display a line under the input field to indicate input focus. You
 * can set the value of this prop to `false` to turn this off. Defaults to `true`.
 * @property {boolean} sortOptions If `true`, the tags will be sorted alphabetically by label or value. Defaults to
 * `true`.
 * @property {node} successMessage A success Notice that will be shown below the component. Defaults to no Notice.
 * @property {string} tagPrefix The string to use identify tags. Defaults to "#".
 * @property {function} tagRenderer The tag renderer callback that will be called for all tags in *value*. Tags will be
 * rendered in the default way if no *tagRenderer* is set.
 * @property {string} title A value which is used as an HTML title attribute for the TagInput field.
 * @property {string|number} value The input value. Supports strings, numbers and objects. If the value is an object
 * the object's `label` attribute will be used as value. You can override this using the *labelKey* prop.
 * @property {string} valueKey The property name that defines the value for your objects in *options*. Defaults to "value".
 * @property {node} warningMessage A warning message that will be shown below the component. Defaults to no warning message.
 *
 * @see Input, Notice
 */
export default class TagInput extends Component {

  static displayName = 'TagInput';

  static propTypes = {
    addable: PropTypes.bool,
    characterCountText: PropTypes.string,
    className: PropTypes.string,
    clearable: PropTypes.bool, // eslint-disable-line
    delimiter: PropTypes.string,
    disabled: PropTypes.bool, // eslint-disable-line
    errorMessage: PropTypes.node, // eslint-disable-line
    iconName: PropTypes.string, // eslint-disable-line
    id: PropTypes.string, // eslint-disable-line
    infoMessage: PropTypes.node, // eslint-disable-line
    labelKey: PropTypes.string,
    locked: PropTypes.bool,
    onChange: PropTypes.func,
    onClearNotice: PropTypes.func,
    onDeleteTag: PropTypes.func,
    onKeyDown: PropTypes.func,
    options: PropTypes.array, // eslint-disable-line
    placeholder: PropTypes.string, // eslint-disable-line
    readOnly: PropTypes.bool,
    required: PropTypes.bool, // eslint-disable-line
    showCharacterCount: PropTypes.bool,
    showOptionsOnFocus: PropTypes.bool, // eslint-disable-line
    showSearch: PropTypes.bool, // eslint-disable-line
    showUnderline: PropTypes.bool, // eslint-disable-line
    successMessage: PropTypes.node, // eslint-disable-line
    title: PropTypes.string,
    tagPrefix: PropTypes.string,
    tagRenderer: PropTypes.func,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    valueKey: PropTypes.string, // eslint-disable-line
    warningMessage: PropTypes.node,
  };

  static defaultProps = {
    characterCountText: '#',
    clearable: true,
    delimiter: DELIMITER,
    labelKey: 'label',
    showUnderline: true,
    tagPrefix: TAG_PREFIX,
    valueKey: 'value',
  };

  static getSelectedTags(value = '', delimiter = DELIMITER, tagPrefix = TAG_PREFIX) {
    return value
      .split(delimiter)
      .map(word => word.trim())
      .filter(word => word.length && word.charAt(0) === tagPrefix && word !== tagPrefix)
      .map(tag => (tag.charAt(0) === tagPrefix && tag.slice(1)) || tag);
  }

  static getTagString(tags = [], delimiter = DELIMITER, tagPrefix = TAG_PREFIX, freeText = '') {
    if (tags.length) {
      const trimmedFreeText = freeText ? freeText.trim() : '';
      return `${tagPrefix}${tags.join(`${delimiter}${tagPrefix}`)}${trimmedFreeText.length ? `${delimiter}${trimmedFreeText}` : ''}`.trim();
    }
    return freeText;
  }

  static getStrings(string = '', delimiter = DELIMITER, tagPrefix = TAG_PREFIX) {
    let index = string.lastIndexOf(tagPrefix);

    if (index >= 0) {
      index = string.indexOf(delimiter, index);
      if (index === -1) {
        return [string, ''];
      }
      return [string.slice(0, index), string.slice(index + 1).trim()];
    }
    return ['', string];
  }

  constructor(props) {
    super(props);
    const [tags, value] = this.parseValue(props.value);
    this.state = {
      tags,
      value,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value === this.props.value) return;
    const [tags, value] = this.parseValue(this.props.value);
    this.setState({ tags, value });
  }

  onChange = (e, autoComplete) => {
    const { delimiter, labelKey, onChange, tagPrefix } = this.props;
    let tags = this.state.tags;
    let value = e.target.value;

    if (e.type === 'clear') {
      tags = [];
      value = '';
    } else if (autoComplete) {
      if (typeof value === 'object') {
        value = value[labelKey];
      }
      if (value) {
        tags.push(value);
      }
      value = '';
    }
    this.setState({ tags, value });
    if (onChange) {
      const tmp = TagInput.getTagString(tags, delimiter, tagPrefix, value);

      onChange(createFakeEvent(tmp, e), autoComplete, this.props.value);
    }
  };

  onKeyDown = e => {
    const { addable, delimiter, tagPrefix, onChange, onKeyDown } = this.props;
    const eventTargetValue = e.target.value; // Needs to be stored. e.target.value in some cases appears to be emptied synchronously when invoking setState() underneath.

    if (e.key === 'Backspace' && e.target.selectionStart === 0 && e.target.selectionEnd === 0) {
      this.onDeleteTag();
    } else if (e.key === 'Enter' && addable && onChange) {
      const tags = TagInput.getSelectedTags(this.props.value, delimiter, tagPrefix);
      const freeText = eventTargetValue.trim().split(delimiter).map(tag => tag.trim()).filter(tag => tag);
      const newTags = freeText.filter(tag => tag.length && tags.indexOf(tag) === -1 && freeText.indexOf(tag) === -1);
      const value = tags
        .concat(newTags)
        .map(tag => `${tagPrefix}${tag}`)
        .join(delimiter);
      const [t1, t2] = this.parseValue(value);

      this.setState({ tags: t1, value: t2 });
      onChange(createFakeEvent(value, e), false, this.props.value, freeText);
    }
    if (onKeyDown) {
      const value = TagInput.getTagString(this.state.tags, this.props.delimiter, this.props.tagPrefix, this.state.value);

      onKeyDown(createFakeEvent(value, e), false, eventTargetValue);
    }
  };

  onDeleteTag = (e, index) => {
    if (!this.props.disabled  && !this.props.readOnly && this.state.tags.length > 0) {
      const { delimiter, onChange, onDeleteTag, tagPrefix } = this.props;
      const tags = this.state.tags.slice();
      let tag = tags[index];
      const dataset = this.refInput && this.refInput.getDataset();

      if (typeof index === 'number') {
        tags.splice(index, 1);
      } else {
        tag = tags.pop();
      }
      this.setState({ tags });
      if (onDeleteTag) {
        onDeleteTag(createFakeEvent(tag, e, dataset));
      }
      if (onChange) {
        const tmp = TagInput.getTagString(tags, delimiter, tagPrefix, this.state.value);

        onChange(createFakeEvent(tmp, e, dataset), true, this.props.value);
      }
    }
  };

  parseValue = value => {
    let tags = [];
    let rest = value || '';

    if (value) {
      const { delimiter, tagPrefix } = this.props;
      let index = value.lastIndexOf(tagPrefix);

      tags = TagInput.getSelectedTags(value, delimiter, tagPrefix);
      if (index > -1) {
        rest = '';
        if ((index = value.indexOf(delimiter, index)) > -1) {
          rest = value.slice(index + delimiter.length);
        }
      }
    }
    return [tags, rest];
  };

  render () {
    const { addable, characterCountText, className, delimiter, labelKey, onChange,  // eslint-disable-line no-unused-vars
      onDeleteTag, options, readOnly, showCharacterCount, title, // eslint-disable-line no-unused-vars
      tagPrefix, tagRenderer, valueKey, ...rest } = this.props; // eslint-disable-line no-unused-vars
    const { value, tags } = this.state;
    let remainingTags;
    const stringValue = this.props[valueKey];

    if (options && options.length) {
      if (typeof options[0] === 'object') {
        remainingTags = options.filter(tag => !tags.find(t => t === tag[labelKey]));
      } else {
        remainingTags = options.filter(tag => tags.indexOf(tag) === -1);
      }
    }
    return (
      <div className={cx('core-tag-input', className, { disabled: rest.disabled || rest.locked, 'read-only': readOnly, tags: tags.length })}>
        {showCharacterCount && (
          <span className="char-count" aria-label="char-count">{characterCountText}{stringValue ? stringValue.length : 0}</span>
        )}
        <Input {...rest} value={value} onChange={this.onChange} showClear={tags.length > 0 || value.length > 0}
          onKeyDown={this.onKeyDown} options={remainingTags} ref={c => (this.refInput = c)} labelKey={labelKey}
          readOnly={readOnly} showCharacterCount={false} title={title}>
          <Tags value={tags} disabled={rest.disabled || rest.locked} onDelete={this.onDeleteTag} itemRenderer={tagRenderer}/>
        </Input>
      </div>
    );
  }

}
